// components/AnonAdoptionPrompt/AnonAdoptionPrompt.tsx — drop 9 prerequisite
// ─────────────────────────────────────────────────────────────────────────
// THE CONFLICT, ASKED ONCE.
//
// Only reached when `adoptionAction` returns `ask`, which means both
// namespaces hold plans and this account has not answered before. Every
// other case is silent: a person with no signed-in plans gets their work
// adopted without being asked, because the question has one useful answer.
//
// ─────────────────────────────────────────────────────────────────────────
// IMPORT IS THE PRIMARY, AND THE ASYMMETRY IS THE REASON.
//
// A wrong "bring it in" leaves an extra plan in a list, which takes one
// click to fix. A wrong "leave it behind" is unrecoverable. When two
// outcomes differ that much in cost, the weighting is not a style choice.
//
// AND DISCARD SAYS SO. "Leave it behind" sounds reversible. It is not, and
// the warning sits with the action rather than in a confirmation step,
// because a second dialog is how people learn to click through the first.
//
// NO "REMEMBER MY CHOICE" CHECKBOX. The choice is remembered either way:
// `conflictAlreadyResolved` records that this account answered, and the
// answer is not asked again. Offering it as an option would imply the
// alternative is being asked every time, which is not on the table.
//
// THIS IS NOT DISMISSABLE BY CLICKING AWAY. There is no close affordance and
// no backdrop dismissal, because there is no neutral outcome: the anonymous
// plan is either brought in or left, and an escape hatch that does neither
// leaves the person in the state the whole feature exists to end. The
// nearest thing to a safe default IS import, and that is the primary.
//
// Copy rule: no em or en dashes in any user-facing string.
// ─────────────────────────────────────────────────────────────────────────

import { useState } from 'react';
import {
	CONFLICT_TITLE,
	CONFLICT_BODY,
	CONFLICT_IMPORT,
	CONFLICT_DISCARD,
	DISCARD_WARNING,
} from '../../lib/anonAdoption';
import styles from './AnonAdoptionPrompt.module.scss';

export interface AnonAdoptionPromptProps {
	/** How many anonymous plans are waiting. Stated, because "a plan" and
	 *  "four plans" are different decisions. */
	anonPlanCount: number;
	/** Runs the adoption sequence. Resolves false when it failed, in which
	 *  case the anonymous copy is untouched and the caller shows the
	 *  failure notice rather than this prompt again. */
	onImport: () => void;
	onDiscard: () => void;
	/** True while the sequence is running, so the buttons cannot be
	 *  double-fired into two writes. */
	busy?: boolean;
}

const AnonAdoptionPrompt = ({
	anonPlanCount,
	onImport,
	onDiscard,
	busy = false,
}: AnonAdoptionPromptProps): JSX.Element => {
	// Discard is deliberately two steps and import is one. Not a
	// confirmation dialog: the second step replaces the button in place, so
	// the warning is read rather than clicked past.
	const [confirmingDiscard, setConfirmingDiscard] = useState(false);

	const what =
		anonPlanCount === 1
			? 'the plan you built'
			: `the ${anonPlanCount} plans you built`;

	return (
		<div
			className={styles.scrim}
			role='dialog'
			aria-modal='true'
			aria-labelledby='anon-adopt-title'>
			<section className={styles.card}>
				<h2
					id='anon-adopt-title'
					className={styles.title}>
					{CONFLICT_TITLE}
				</h2>
				<p className={styles.body}>{CONFLICT_BODY}</p>
				<p className={styles.detail}>
					This will bring in {what} before signing in. Your existing
					plans stay exactly as they are.
				</p>

				<div className={styles.actions}>
					<button
						type='button'
						className={styles.primary}
						disabled={busy}
						onClick={onImport}>
						{CONFLICT_IMPORT}
					</button>

					{confirmingDiscard ? (
						<button
							type='button'
							className={styles.destructive}
							disabled={busy}
							onClick={onDiscard}>
							Yes, leave it behind
						</button>
					) : (
						<button
							type='button'
							className={styles.quiet}
							disabled={busy}
							onClick={() => setConfirmingDiscard(true)}>
							{CONFLICT_DISCARD}
						</button>
					)}
				</div>

				{confirmingDiscard && (
					<p className={styles.warning}>{DISCARD_WARNING}</p>
				)}
			</section>
		</div>
	);
};

export default AnonAdoptionPrompt;
